"use client";

import React, { useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { clsx } from "clsx";
import {
  FiHome,
  FiUsers,
  FiCreditCard,
  FiFileText,
  FiLogOut,
  FiArchive,
  FiCalendar,
  FiBookOpen,
  FiUser,
  FiChevronUp,
  FiMonitor,
  FiSun,
  FiMoon,
  FiEdit3,
  FiTag,
  FiLayers,
  FiShield,
  FiList,
  FiMessageSquare,
  FiBarChart2,
  FiActivity,
} from "react-icons/fi";
import { authService } from "@/lib/api/auth.service";
import { toast } from "react-toastify";
import { useTheme, type Theme } from "@/components/ThemeProvider";

const menuItems = [
  { title: "대시보드", href: "/", icon: FiHome },
  { title: "트래픽 분석", href: "/traffic", icon: FiActivity },
  { title: "사용자 관리", href: "/users", icon: FiUsers },
  { title: "결제 관리", href: "/payments", icon: FiCreditCard },
  { title: "아티팩트", href: "/artifacts", icon: FiArchive },
  {
    title: "위험성평가 설문",
    href: "/risk-assessment-surveys",
    icon: FiMessageSquare,
  },
  {
    title: "SUS 사용성 설문",
    href: "/sus-surveys",
    icon: FiBarChart2,
  },
  { title: "게시판 관리", href: "/board", icon: FiFileText },
  { title: "가격 플랜 관리", href: "/pricing-plans", icon: FiCreditCard },
  {
    title: "요금제 서비스 그룹",
    href: "/pricing-service-groups",
    icon: FiLayers,
  },
  { title: "프로모션·쿠폰", href: "/subscription-campaigns", icon: FiTag },
  { title: "마케팅 이벤트", href: "/events", icon: FiCalendar },
  { title: "관리자 역할", href: "/admin-roles", icon: FiShield },
  { title: "감사 로그", href: "/audit", icon: FiList },
  { title: "구독 관리", href: "/subscriptions", icon: FiCalendar },
  { title: "블로그 관리", href: "/blog", icon: FiBookOpen },
  { title: "AI 피드 생성", href: "/blog-feed", icon: FiEdit3 },
  { title: "회사소식 관리", href: "/notice", icon: FiFileText },
];

const themeOptions: { value: Theme; label: string; Icon: React.ElementType }[] =
  [
    { value: "system", label: "시스템", Icon: FiMonitor },
    { value: "light", label: "라이트", Icon: FiSun },
    { value: "dark", label: "다크", Icon: FiMoon },
  ];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const pathname = usePathname();
  const router = useRouter();
  const { theme, setTheme } = useTheme();
  const [panelOpen, setPanelOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await authService.logout();
      toast.success("로그아웃되었습니다.");
      router.push("/login");
    } catch {
      toast.error("로그아웃에 실패했습니다.");
    }
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 backdrop-blur-sm dark:bg-black/70 lg:hidden"
          onClick={onClose}
        />
      )}

      <div
        className={clsx(
          "fixed top-0 left-0 z-30 h-full w-64 bg-white dark:bg-gray-900 shadow-lg border-r border-gray-200 dark:border-gray-700 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0",
          isOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-center h-16 px-4 border-b border-gray-200 dark:border-gray-700">
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              SEEXR 관리자
            </h1>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 overflow-y-auto">
            <ul className="space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive =
                  item.href === "/"
                    ? pathname === "/"
                    : pathname === item.href ||
                      pathname.startsWith(item.href + "/");
                return (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      className={clsx(
                        "flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors",
                        isActive
                          ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800"
                          : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800",
                      )}
                      onClick={() => {
                        if (window.innerWidth < 1024) onClose();
                      }}
                    >
                      <Icon className="w-5 h-5 mr-3 shrink-0" />
                      {item.title}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* User panel footer */}
          <div className="border-t border-gray-200 dark:border-gray-700">
            {/* Slide-up panel */}
            {panelOpen && (
              <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                {/* Theme toggle */}
                <span className="block text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5 px-1">
                  테마
                </span>
                <div className="flex items-center gap-1 p-1 rounded-lg bg-gray-100 dark:bg-gray-800">
                  {themeOptions.map(({ value, label, Icon }) => (
                    <button
                      key={value}
                      onClick={() => setTheme(value)}
                      className={clsx(
                        "flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md text-[11px] font-semibold transition-all cursor-pointer",
                        theme === value
                          ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
                          : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200",
                      )}
                    >
                      <Icon size={12} />
                      {label}
                    </button>
                  ))}
                </div>

                {/* Logout */}
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full mt-2 px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <FiLogOut className="w-4 h-4 mr-2" />
                  로그아웃
                </button>
              </div>
            )}

            {/* User button */}
            <button
              onClick={() => setPanelOpen((o) => !o)}
              className="flex items-center w-full px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <div className="w-7 h-7 rounded-full bg-blue-600 dark:bg-blue-500 flex items-center justify-center mr-2.5 shrink-0">
                <FiUser className="w-4 h-4 text-white" />
              </div>
              <span className="flex-1 text-left">관리자</span>
              <FiChevronUp
                className={clsx(
                  "w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform",
                  panelOpen ? "rotate-0" : "rotate-180",
                )}
              />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
